import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import NotFoundPage from '../../../NotFoundPage';
import { BoxRadioButton, RadioButtons, TransparentOverlay, VersionFileAttachArea, } from '../../../borispage/common/AdminCommonComponents';
import { gconf } from '../../../conf/gconf';
import { getSiteLanguageFullNameTypeLangKey, SiteLanguageType } from '../../../definitions/SiteLanguageType';
import { docwriteutil, handleError401, } from '../../../docwrite/community/common/docwriteutil';
import { useZstdDocWrite } from '../../../docwrite/community/common/useZstdDocWrite';
import { DocWriteContainer } from '../../../docwrite/community/stylecomp/DocWriteContainer';
import { Title, SubTitle, TitleWrap } from '../../../docwrite/community/toprow/DocWriteBtns';
import { Body } from '../../../docwrite/community/toprow/DocWriteTopRow1';
import SsqEditor5 from '../../../libraries/SsqEditor5';
import { IfLoginInfo } from '../../../models/IfLoginInfo';
import svcs from '../../../services';
import { loginutil } from '../../../util/loginutil';
import { Btn, InputWrapFortoggle } from '../../../style/CommonStyledComponents';
import { fontFamilyBold, fontFamilyRegular } from '../../../style/fonts';
import img_date_picker from '../../../images/common/icon_date_picker.svg';
import { ButtonsContainer, TemporaryDataPopup } from '../../../borispage/admintab/doctab/AdminNewDocumentPopupPage';
import { formatDateTime } from '../../../util/formatDateTime';
import { langutil } from '../../../language/langutil';
import deleteIconGray from '../../../images/common/delete-icon-gray.svg';
import { useShowToast } from '../../../hooks/useToast';
import { EmptyPolicyUpdateReq, PolicyCreateReq, PolicyFileReq, PolicyFileRes, PolicyUpdateReq, PolicyVersionRes } from 'communitypage/privacypolicy/types/policy.types';
import { IFile } from 'repositories/SsqDataRepository';
import { usePolicyApi } from 'communitypage/privacypolicy/hooks/useApiPrivacyPolicyPage';

interface IRouteMatchParams {
  documentId?: string;
}

const PrivacyPolicyWritePage = () => {
  const zstd = useZstdDocWrite();
  const params = useParams() as unknown as IRouteMatchParams;
  const showToast = useShowToast();
  const location = useLocation();
  const navigate = useNavigate();
  const modifyId = Number(params.documentId);
  const trans = docwriteutil.getadmintrans;

  const { getAdminPolicyDetail, getAdminPolicyList, createPolicy, updatePolicy, deletePolicy } = usePolicyApi();

  const loginInfo = useSelector((state: any) => state.main.loginInfo) as IfLoginInfo;

  const { uploadingFiles, setUploadingFiles, running, setRunning } = zstd;

  const [notFound, setNotFound] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(!!modifyId);

  const refBody = useRef<HTMLDivElement>(null);
  const prevLoginInfoRef = useRef<IfLoginInfo | null>(null);
  const prevLocationPathnameRef = useRef<string | null>(null);
  const onBeforeUnloadHandlerRef = useRef<(e: any) => void>();

  type PageTypeLocal = 'NEW' | 'MODIFY' | 'TEMP';
  const [pageType, setPageType] = useState<PageTypeLocal>('NEW');

  const [policyReq, setPolicyReq] = useState<PolicyUpdateReq>(EmptyPolicyUpdateReq);
  const [tempPolicyList, setTempPolicyList] = useState<PolicyVersionRes[]>([]);
  const [showTempPopup, setShowTempPopup] = useState(false);
  const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());

  const [validation, setValidation] = useState<PolicyValidation>({});
  const [hoveredTempId, setHoveredTempId] = useState<number | null>(null);

  interface PolicyValidation {
    expose?: boolean;
    revisionTime?: boolean;
    effectiveTime?: boolean;
    version?: boolean;
    exposeSiteLanguage?: boolean;
  }

  const validate = useCallback(() => {
    const versionRegex = /^[0-9]+\.[0-9]+$/; // "숫자.숫자" 형태

    const newValidation: PolicyValidation = {
      expose: policyReq.expose === undefined || policyReq.expose === null,
      revisionTime: !policyReq.revisionTime,
      effectiveTime: !policyReq.effectiveTime,
      version: !policyReq.version || !versionRegex.test(policyReq.version),
      exposeSiteLanguage: policyReq.exposeSiteLanguage !== SiteLanguageType.EN_US,
    };

    setValidation(newValidation);
    return Object.values(newValidation).some((v) => v);
  }, [policyReq]);

  const loadTempList = useCallback(async () => {
    try {
      const param = { isTemp: true, exposeSiteLanguage: 'en-US' };
      const list = await getAdminPolicyList(param);
      setTempPolicyList(list || []);
    } catch (e) {
      console.error('임시저장 리스트 로딩 실패', e);
    }
  }, []);

  async function createPrivacyPolicy() {
    try {
      if (!policyReq.version) {
        console.warn('version 값 없음');
        return;
      }

      const formatDate = (date: string) => (date ? new Date(date).toISOString() : '');

      let data;
      if (modifyId) {
        const param: PolicyUpdateReq = {
          ...policyReq,
          isTemp: false,
          id: modifyId,
          originId: policyReq.originId,
          revisionTime: formatDate(policyReq.revisionTime),
          effectiveTime: formatDate(policyReq.effectiveTime),
          exposeSiteLanguage: policyReq.exposeSiteLanguage || 'en-US',
        };

        data = await updatePolicy(modifyId, param);
      } else {
        const param: PolicyCreateReq = {
          ...policyReq,
          isTemp: false,
          revisionTime: formatDate(policyReq.revisionTime),
          effectiveTime: formatDate(policyReq.effectiveTime),
          exposeSiteLanguage: policyReq.exposeSiteLanguage || 'en-US',
        };

        data = await createPolicy(param);
      }

      console.log('정책 생성 성공', data);
    } catch (e) {
      console.error('글 업로드 실패', e);
      throw e;
    }
  }

  async function saveTempPolicy(): Promise<number | undefined> {
    try {
      if (!policyReq.version) {
        setValidation((prev) => ({
          ...prev,
          version: true,
        }));

        alert(trans('I18N_PRIVACY_POLICY_VERSION_REQUIRED'));
        return;
      }

      const formatDate = (date: string) => (date ? new Date(date).toISOString() : '');

      let data;

      console.log('modifyId: ', modifyId, 'policyReq.id: ', policyReq.id, 'policyReq.originId: ', policyReq.originId);

      if (pageType === 'NEW') {
        // 새 글 → 임시저장: POST /create (isTemp: true)
        const param: PolicyCreateReq = {
          ...policyReq,
          isTemp: true,
          revisionTime: formatDate(policyReq.revisionTime),
          effectiveTime: formatDate(policyReq.effectiveTime),
          exposeSiteLanguage: policyReq.exposeSiteLanguage || 'en-US',
        };

        data = await createPolicy(param);

        console.log('createPolicy response (NEW → TEMP):', data);
        showToast('msg', trans('I18N_PRIVACY_POLICY_TEMP_SAVE_SUCCESS'));

        return data;
      } else {
        // TEMP → 임시저장: PUT /update/{id} (isTemp: true)
        const param: PolicyUpdateReq = {
          ...policyReq,
          isTemp: true,
          id: modifyId,
          revisionTime: formatDate(policyReq.revisionTime),
          effectiveTime: formatDate(policyReq.effectiveTime),
          exposeSiteLanguage: policyReq.exposeSiteLanguage || 'en-US',
        };

        data = await updatePolicy(modifyId, param);

        console.log('updatePolicy response (' + pageType + ' → TEMP):', data);
        showToast('msg', trans('I18N_PRIVACY_POLICY_TEMP_SAVE_SUCCESS'));

        if (data !== modifyId && data) {
          return data;
        }
      }
    } catch (e) {
      console.error('정책 임시저장 실패', e);
    }
  }

  useEffect(() => {
    onBeforeUnloadHandlerRef.current = (e: any) => {
      if (!loginutil.islogin(loginInfo)) {
        return;
      }
      if (gconf.error401) {
        return;
      }
      e.preventDefault();
      e.returnValue = '';
    };
  }, [loginInfo]);


  const stableOnBeforeUnload = useCallback((e: any) => {
    onBeforeUnloadHandlerRef.current?.(e);
  }, []);

  useEffect(() => {
    if (!modifyId || modifyId === 0) {
      setPageType('NEW');
      console.log("lang", lang);
      setPolicyReq({
        ...EmptyPolicyUpdateReq,
        exposeSiteLanguage: country === 'us' ? 'en-US' : '', // 이후 모든 사이트 open 후 수정
      });
      return;
    }

    const fetchDetail = async () => {
      try {
        setIsPageLoading(true);

        const detail = await getAdminPolicyDetail(modifyId);

        setPolicyReq((prev) => ({
          ...prev,
          version: detail.version,
          expose: detail.expose,
          revisionTime: detail.revisionTime?.slice(0, 10),
          effectiveTime: detail.effectiveTime?.slice(0, 10),
          description: detail.description,
          exposeSiteLanguage: detail.exposeSiteLanguage,
          files: detail.files || [],
          originId: detail.originId,
          id: detail.id,
        }));

        if (detail.originId) {
          setPageType('TEMP');
        } else {
          setPageType('MODIFY');
        }
      } catch (e) {
        console.error('detail fetch 실패', e);
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchDetail();
  }, [params.documentId, modifyId]);

  const doInit = useCallback(async () => {
    try {
      setIsPageLoading(true);

      if (!loginInfo?.userId) {
        console.log('loginInfo is empty');
        if (loginInfo?.apires === 'Y') {
          navigate(`/requireMemberLogin`, { state: location.pathname });
        }
        return;
      }

      if (!loginInfo?.allowAdminManage) {
        navigate(`/requireMemberLogin`, { state: location.pathname });
        return;
      }

      await loadTempList();

      if (modifyId > 0 && modifyId != undefined) {

          const detail = await getAdminPolicyDetail(modifyId);

          setPolicyReq((prev) => ({
            ...prev,
            version: detail.version,
            expose: detail.expose,
            revisionTime: detail.revisionTime?.slice(0, 10),
            effectiveTime: detail.effectiveTime?.slice(0, 10),
            description: detail.description,
            exposeSiteLanguage: detail.exposeSiteLanguage,
            files: detail.files || [],
            originId: detail.originId,
          }));

          if (detail.originId) {
            setPageType('TEMP');
          } else {
            setPageType('MODIFY');
          }
        }

      console.log('doInit pageType', pageType);

      window.removeEventListener('beforeunload', stableOnBeforeUnload);
      window.addEventListener('beforeunload', stableOnBeforeUnload);

    } finally {
      setIsPageLoading(false);
    }
  }, [location.search, location.pathname, loginInfo, navigate, params, stableOnBeforeUnload]);

  const onSave = useCallback(async () => {
    console.log('save start..', policyReq);

    const hasError = validate();

    if (hasError) {
      alert(trans('I18N_PRIVACY_POLICY_REQUIRED_FIELDS'));
      return;
    }

    const isModify = policyReq.id && policyReq.id > 0;
    if (isModify) {
      if (!confirm(trans('I18N_DOCWRITE_OVERWRITE_MSG'))) {
        return;
      }
    }

    if (running) {
      console.warn('post save running.');
      return;
    }

    setRunning(true);

    try {
      await createPrivacyPolicy();

      showToast('msg', trans('I18N_PRIVACY_POLICY_SAVE_SUCCESS'));
      navigate(`/${country}/${lang}/privacypolicy`);
    } catch (e: any) {
      console.error(e);

      const status = e?.response?.status;
      const message = e?.response?.data?.message;

      if (status === 500) {
        console.error('500 error', status, message);
        alert(trans('I18N_PRIVACY_POLICY_VERSION_DUPLICATE'));
      } else {
        alert(trans('I18N_PRIVACY_POLICY_SAVE_ERROR'));
      }
    } finally {
      setRunning(false);
    }
  }, [policyReq, running]);

  const onUploadFiles = useCallback(
    async (files: any): Promise<PolicyFileReq[]> => {
      try {
        setUploadingFiles(true);
        const key = `privacypolicy/${Date.now()}`;

        const uploaded = await svcs.repoSsqData.postFileInCdnBlob(key, files);
        console.log(uploaded);

        return uploaded.map((f: any, idx: number) => ({
          fileName: f.fileName,
          fileKey: f.fileKey,
          fileSize: files[idx].size,
        }));
      } catch (e) {
        console.error('파일 업로드 실패', e);
        return [];
      } finally {
        setUploadingFiles(false);
      }
    },
    [setUploadingFiles],
  );

  const uploadAttachedFilesAndAddFilesRow = useCallback(
    async (files: any) => {
      const totalFileLength = files.length + (policyReq.files?.length || 0);

      if (totalFileLength > 10) {
        alert(trans('I18N_ATTACH_ALERT'));
        return;
      }

      const uploadedFiles = await onUploadFiles(files);

      setPolicyReq((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...uploadedFiles],
      }));
    },
    [policyReq.files, onUploadFiles],
  );
  const onDeleteFile = useCallback(async (file: PolicyFileRes) => {
    // UI에서 파일 목록 제거
    setPolicyReq((prev) => ({
      ...prev,
      files: (prev.files || []).filter((f) => f.fileKey !== file.fileKey),
    }));
  }, []);

  const handleBtnCmd = useCallback(
    async (cmd: string, param?: any) => {
      console.log('handle btn cmd ', cmd, ' param ', param);
      if (cmd === 'tmp-save') {
        const savedId = await saveTempPolicy();

        await loadTempList();

        if (savedId !== modifyId && savedId) {
          navigate(`/${country}/${lang}/privacypolicy/new/${savedId}`);
        }
      } else if (cmd === 'save') {
        await onSave();
      } else {
        console.error('unknown cmd ', cmd, ' param', param);
      }
    },
    [pageType, policyReq, onSave, modifyId],
  );


  const deleteTempPolicy = async (id: number) => {
    try {
      await deletePolicy(id);
      await loadTempList();

      if (id === modifyId) {
        navigate(`/${country}/${lang}/privacypolicy/new/0`);
      }
    } catch (e) {
      console.error('임시 정책 삭제 실패', e);
    }
  };

  // componentDidMount effect
  useEffect(() => {
    console.log('CmmntyDocWritePageBody componentDidMount');
    doInit();

    gconf.handleError401 = handleError401;

    return () => {
      window.removeEventListener('beforeunload', stableOnBeforeUnload);
      gconf.handleError401 = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prevLoginInfo = prevLoginInfoRef.current;
    const prevLocationPathname = prevLocationPathnameRef.current;

    if (
      (loginInfo?.userId && loginInfo.userId !== prevLoginInfo?.userId) ||
      location.pathname !== prevLocationPathname
    ) {
      if (prevLoginInfo !== null || prevLocationPathname !== null) {
        doInit();
      }
    }

    prevLoginInfoRef.current = loginInfo;
    prevLocationPathnameRef.current = location.pathname;
  }, [loginInfo?.userId, location.pathname, doInit]);

  // ─── 렌더링 ───

  if (notFound) return <NotFoundPage />;
  if (!policyReq) return null;
  if (!loginInfo?.allowAdminManage) return null;

  const TempDataPopupWrap = ({
    trans,
    temporaryPolicyList,
    history,
  }: {
    trans: Function;
    temporaryPolicyList: PolicyVersionRes[];
    history: any;
  }) => {
    return (
      <CustomTempPopupWrap>
        <p className={'tit'}>{trans('I18N_SAVE_LIST')}</p>

        {temporaryPolicyList.map((item, index) => {
          const isHovered = hoveredTempId === item.id;
          const isActive = item.id === modifyId;

          return (
            <div
              key={`temp-${index}`}
              className={isActive ? 'active' : ''}
              onMouseEnter={() => setHoveredTempId(item.id)}
              onMouseLeave={() => setHoveredTempId(null)}
              onClick={() => {
                if (!confirm(trans('I18N_PRIVACY_POLICY_LOAD_TEMP_CONFIRM'))) return;
                const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());
                const url = `/${country}/${lang}/privacypolicy/new/${item.id}`;

                if (url === history?.location?.pathname) {
                  window.location.reload();
                } else {
                  history.push(url);
                }
              }}
            >
              <div className="Tempo_item_tit_wrap">
                <span className="Tempo_item_version">{item.version || trans('I18N_NO_TITLE')}</span>

                <span className="Tempo_item_new">{item.originId == item.id ? 'New' : 'Update'}</span>

                {isHovered && (
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(trans('I18N_PRIVACY_POLICY_DELETE_CONFIRM'))) {
                        deleteTempPolicy(item.id);
                      }
                    }}
                  >
                    <img src={deleteIconGray} alt="delete" />
                  </button>
                )}
              </div>

              <span className="Tempo_item_time">{formatDateTime(new Date(item.updateTime))}</span>
            </div>
          );
        })}

        <p className={'txt'}>{trans('I18N_SAVE_LIST_DESC')}</p>
      </CustomTempPopupWrap>
    );
  };

  const mapToIFile = (files: PolicyFileReq[]): IFile[] => {
    return files.map((f) => ({
      fileName: f.fileName,
      fileKey: f.fileKey,
      fileSize: f.fileSize,

      id: 0,
      blobUrlForLargeFile: '',
      fileLang: 'KO',
      fileType: 'NONE',
      targetSoftwareType: 'NONE',
      downloadCount: 0,
      writerOnly: false,

      searchExpose: false,
    }));
  };

  return (
    <DocWriteContainer className={'Admin_newdocument_wrap'}>
      <div className={'container'}>
        <ResponsiveButtonsContainer className={scrollY ? 'sticky' : ''}>
          <div className="container">
            <TitleWrap className={'title-wrap'}>
              <Title>
                {pageType === 'NEW' && trans('I18N_DOCWRITE_NEW_TITLE')}
                {pageType === 'MODIFY' && (
                  <>
                    {trans('I18N_DOCWRITE_MOD_TITLE')}
                    <OriginalLink
                      onClick={() => window.open(`/${country}/${lang}/privacypolicy/${policyReq.id}`, '_blank')}
                    >
                      {trans('I18N_PRIVACYPOLICY_VIEW_ORIGINAL')}
                    </OriginalLink>
                  </>
                )}
                {pageType === 'TEMP' && policyReq.originId === policyReq.id && 'Temp (New)'}
                {pageType === 'TEMP' && policyReq.originId !== policyReq.id && (
                  <>
                    {trans('I18N_PRIVACYPOLICY_TEMP_MOD_TITLE')}
                    <OriginalLink
                      onClick={() => window.open(`/${country}/${lang}/privacypolicy/${policyReq.originId}`, '_blank')}
                    >
                      {trans('I18N_PRIVACYPOLICY_VIEW_ORIGINAL')}
                    </OriginalLink>
                  </>
                )}
              </Title>
              <SubTitle>
                {pageType === 'NEW' && trans('I18N_DOCWRITE_NEW_SUBTITLE')}
                {pageType === 'MODIFY' && trans('I18N_DOCWRITE_MOD_SUBTITLE')}
                {pageType === 'TEMP' && policyReq.originId === policyReq.id && trans('I18N_PRIVACYPOLICY_TEMP_NEW_SUBTITLE')}
                {pageType === 'TEMP' && policyReq.originId !== policyReq.id && trans('I18N_DOCWRITE_MOD_SUBTITLE')}
              </SubTitle>
            </TitleWrap>
            <div className="button-wrap">
              <div className={'tempo-button-wrap'}>
                <Btn
                  className={'batch-button'}
                  onClick={async () => {
                    await handleBtnCmd('tmp-save');
                  }}
                >
                  {docwriteutil.getadmintrans('I18N_BUTTON_TEMP_SAVE')}
                </Btn>

                <Btn
                  className={'tempo-count-button'}
                  onClick={async () => {
                    setShowTempPopup(!showTempPopup);
                    await loadTempList();
                  }}
                >
                  {tempPolicyList?.length || 0}
                </Btn>

                {showTempPopup && tempPolicyList.length !== 0 && (
                  <TempDataPopupWrap
                    temporaryPolicyList={tempPolicyList}
                    trans={docwriteutil.getadmintrans}
                    history={{
                      push: (path: string) => navigate(path),
                      location: { pathname: location.pathname },
                    }}
                  />
                )}

                {showTempPopup && tempPolicyList.length !== 0 && (
                  <TransparentOverlay className={'Tempo_overlay'} onClick={() => setShowTempPopup(false)} />
                )}
              </div>

              {/* 저장 */}
              <Btn
                className={'post-button'}
                onClick={() => handleBtnCmd('save')}
              >
                {policyReq.id && policyReq.id > 0
                  ? docwriteutil.getadmintrans('I18N_DOCWRITE_BTN_REGIST_MOD')
                  : docwriteutil.getadmintrans('I18N_DOCWRITE_BTN_REGIST')}
              </Btn>
            </div>
          </div>
        </ResponsiveButtonsContainer>

        <div>
          <Body className={'approval_row flex_row approval_row_shadow'}>
            <InputWrapFortoggle>
              <div className={'approval_tit'}>{trans('I18N_FILTER_EXPOSE_STATE')}</div>
              <input
                aria-label="checkbox-expose"
                type={'checkbox'}
                id={'toggle'}
                checked={!policyReq.expose}
                onChange={() =>
                  setPolicyReq((prev) => ({
                    ...prev,
                    expose: !prev.expose,
                  }))
                }
              />
              <label htmlFor={'toggle'}>
                {trans(policyReq.expose ? 'I18N_FILTER_EXPOSE' : 'I18N_FILTER_NOT_EXPOSE')}
              </label>
            </InputWrapFortoggle>
          </Body>

          <TopRow2Wrap className={'approval_row flex_row approval_row_shadow'}>
            <Row1>
              <div className={'flex col1'}>
                <div className={'approval_tit'}>{trans('I18N_BOARD')}</div>
                <div className={'approval_tit'}>{trans('I18N_PRIVACY_POLICY_BOARD')}</div>
              </div>
            </Row1>
            <Row1>
              {/*사이트 선택*/}
              <div className={'radio_wrap col1'}>
                <div className={'approval_tit'}>{trans('I18N_FILTER_REGION_LANGUAGE')}</div>
                <div className={`radio-buttons-wrap ${validation.exposeSiteLanguage ? 'error' : ''}`}>
                  <RadioButtons
                    id={'site-language'}
                    selectedValue={policyReq.exposeSiteLanguage}
                    valueList={[SiteLanguageType.KO_KR, SiteLanguageType.EN, SiteLanguageType.EN_US]}
                    getDisplayValue={(lang: string) => trans(getSiteLanguageFullNameTypeLangKey(lang))}
                    onClickItem={(value: any) => {
                      if (value !== SiteLanguageType.EN_US) return;
                      setPolicyReq((prev) => ({
                        ...prev,
                        exposeSiteLanguage: value,
                      }));
                    }}
                  />
                </div>
              </div>
            </Row1>
            <Row2>
              <div className={'flex col1'}>
                <div className={'approval_tit'}>{trans('I18N_REVISION_DATE')}</div>
                <div className={'template-wrap'}>
                  <input
                    type="date"
                    className={`date-picker ${validation.revisionTime ? 'error' : ''}`}
                    value={policyReq.revisionTime}
                    onChange={(e) => {
                      setPolicyReq((prev) => ({
                        ...prev,
                        revisionTime: e.target.value,
                      }));
                      setValidation((prev) => ({ ...prev, revisionTime: false }));
                    } }
                  />
                </div>
              </div>
              <div className={'flex col2'}>
                <div className={'approval_tit'}>{trans('I18N_EFFECTIVE_DATE')}</div>
                <div className={'template-wrap'}>
                  <input
                    type="date"
                    className={`date-picker ${validation.effectiveTime ? 'error' : ''}`}
                    value={policyReq.effectiveTime}
                    onChange={(e) => {
                      setPolicyReq((prev) => ({
                        ...prev,
                        effectiveTime: e.target.value,
                      })) ;
                      setValidation((prev) => ({ ...prev, effectiveTime: false }));
                    }}
                  />
                </div>
              </div>
              <div className={'flex col3'}>
                <div className={'approval_tit'}>{trans('I18N_PRIVACY_POLICY_VERSION')}</div>
                <div className={`input_version ${validation.version ? 'error' : ''}`}>
                  <input
                    type="text"
                    className="version"
                    value={policyReq.version}
                    placeholder="ex) 1.0"
                    onChange={(e) => {
                      const value = e.target.value;
                      // 숫자.숫자 형태만 허용 (입력 중간 상태도 허용)
                      if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                        setPolicyReq((prev) => ({ ...prev, version: value }));
                        setValidation((prev) => ({ ...prev, version: false }));
                      }
                    } }
                  />
                </div>
                <div className={'description_version'}> {trans('I18N_PRIVACY_POLICY_VERSION_DESC')} </div>
              </div>
            </Row2>
          </TopRow2Wrap>

          <div className={'editor_txt_wrap'} style={{ paddingTop: '20px' }}>
            <div className={'editor_txt '} ref={refBody}>
              {!isPageLoading && policyReq.description !== undefined && (
              <SsqEditor5
                // key={policyReq.description ? 'loaded' : 'empty'}
                key={modifyId || 'new'}
                contents={policyReq.description}
                width={'100%'}
                height={'570px'}
                lang={svcs.svcLang.lang()}
                onChangeData={(data: any) => {
                  setPolicyReq((prev) => ({
                    ...prev,
                    description: data,
                  }));
                }}
              />
              )}
            </div>
          </div>

          <div>
            <HiddenRadioWrapper>
              <div className="sample-attach" style={{ marginTop: '1.6rem', padding: '20px' }}>
                <VersionFileAttachArea
                  files={mapToIFile(policyReq.files || [])}
                  versionIndex={0}
                  onDeleteFile={async (file) => {
                    await onDeleteFile(file);
                  }}
                  onUploadFile={uploadAttachedFilesAndAddFilesRow}
                  setFileLang={() => {}}
                  setFileType={() => {}}
                  setTargetSoftwareType={() => {}}
                  readonly={false}
                />
              </div>
            </HiddenRadioWrapper>
          </div>
        </div>
      </div>
    </DocWriteContainer>
  );
};

export default PrivacyPolicyWritePage;


const TopRow2Wrap = styled.div`
  &&& {
    height: auto;

    .error-message-inquiry {
      color: #e6003e;
      position: absolute;
      font-size: 10px;
    }

    input[type='date'].date-picker {
      position: relative;
      border: 1px solid #c7d6ea;
      border-radius: 0.4rem;
      height: 3.2rem;
      line-height: 3.2rem;
      padding-left: 3rem;
      font-size: 1.2rem;
      ${fontFamilyRegular};
      color: #000000;
    }

    input[type='date'].date-picker.error {
      border: 1px solid #e6003e;
    }

    input[type='date']::-webkit-calendar-picker-indicator {
      background: url(${img_date_picker}) transparent no-repeat 0.6rem 0.3rem;
      position: absolute;
      cursor: pointer;
      left: 0;
      top: 0px;
      width: 100%;
      height: 100%;
    }

    .sample-attach [class*='radio'] {
      display: none;
    }

    .select-sm {
      padding: 0 2.4rem 0 1.2rem !important;
      font-size: 1.2rem;
      font-weight: 500;
    }

    @media screen and (max-width: 764px) {
      input[type='date'].date-picker {
        top: -2px;
        background: url(${img_date_picker}) transparent no-repeat 0.2rem 0;
      }

      input[type='date']::-webkit-calendar-picker-indicator {
        background: none;
      }
    }
  }
`;

const Row1 = styled.div`
  & {
    width: 100% !important;
    margin-bottom: 1.6rem;
    float: none !important;

    > .flex {
      margin-bottom: 1.6rem;
      &:has(button) {
        width: 40%;
      }
    }
  }

  .col1 {
    display: flex;
    margin-bottom: 1.6rem;
  }

  .radio-buttons-wrap.error {
    border: 1px solid #e6003e;
    border-radius: 0.4rem;
    padding: 0.1rem 0.1rem;
  }

  .radio_wrap {
    // en-US를 제외한 모든 라디오 버튼을 비활성화 스타일로
    input[id^='site-language-']:not([id='site-language-en-US']) + label {
      background: #eee !important;
      color: #999 !important;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  @media screen and (max-width: 1199px) {
    flex-wrap: wrap;
    margin-bottom: 0 !important;
    > .flex {
      width: 100% !important;
      margin-bottom: 1.6rem;
    }
  }
`;

const Row2 = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.6rem;

  &&& {
    width: 100%;
    float: none;

    .col1 {
      width: 30%;
      display: flex;
    }

    .col2 {
      width: 30%;
      display: flex;
    }

    .col3 {
      display: flex;
      align-items: center;
    }

    .col1,
    .col2,
    .col3 {
      display: flex;
      align-items: center;
      gap: 0.8rem;
    }

    .description_version {
      text-align: center;
      padding-left: 10px;
      color: #767676;
    }

    .input_version {
      width: 60px;
      padding: 4px 8px;

      border: 1px solid #cfcfcf;
      border-radius: 3px;

      box-sizing: border-box;

      display: flex;
      align-items: center;
    }

    .input_version.error {
      border: 1px solid #e6003e;
    }
    
    .version {
      width: 100%;
      font-size: 12px;
      font-weight: 100;
      text-align: center;
    }
    
  }

  @media screen and (max-width: 764px) {
    flex-wrap: wrap;
    .col1 {
      width: 100% !important;
      margin-top: 0rem !important;
    }

    .col2 {
      width: 100% !important;
      margin-top: 1.6rem;
    }
  }
`;

const ResponsiveButtonsContainer = styled(ButtonsContainer)`
  > .container {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
  
`;

const OriginalLink = styled.span`
  margin-left: 1.2rem;
  font-size: 1.2rem;
  font-weight: normal;
  color: #407edb;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.7;
  }
`;


const HiddenRadioWrapper = styled.div`
  ${BoxRadioButton} {
    display: none !important;
  }

  .remove_btn {
    background: none;
    border: none;
    cursor: pointer;
  }

  .file-name {
    all: unset;
  }

  .file-size {
    margin-left: 2rem;
  }

  .file-item .file-name {
    font-size: 1.2rem;
    background: rgba(199, 214, 234, 0.5);
    border: 1px solid rgb(199, 214, 234);
    border-radius: 4px;
    width: 32.8rem;
    height: 3.2rem;
    line-height: 3.2rem;
    color: rgb(64, 125, 217);
    font-family: 'Pretendard Bold', sans-serif;
    font-style: normal;
    display: inline-block;
    padding: 0px 1.2rem;
    vertical-align: middle;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .file-item {
    margin-bottom: 1rem;
  }

  .add-file-version-0 {
    cursor: pointer;
    width: 7.6rem;
    height: 3.2rem;
    size: 1.2rem;
    color: #407edb;
    border-radius: 0.4rem;
  }

  .file_add_btn {
    color: #407edb;
    ${fontFamilyBold};
    font-size: 1.2rem;
    line-height: 16px;

    img {
      margin-right: 0.8rem;
      vertical-align: top;
    }
  }
`;

const CustomTempPopupWrap = styled(TemporaryDataPopup)`
  .Tempo_item_tit_wrap {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .delete-btn {
    margin-left: auto;
  }

  .txt {
    display: none;
  }

  > div.active {
    background-color: #e8f0fe;
    border-left: 3px solid #407edb;
  }
`;
